/**
 * `GET /overview/pillars` — slice 70. The four pillar cards, each saying what
 * its pillar is doing right now, from real rows.
 *
 * ## WHY A SECOND READ BESIDE `overview.pillars`
 *
 * `GET /overview` already carries `pillars: OverviewPillar[]` — built, entitled,
 * available. That answers "may I go there". The owner's brief for slice 70 asks
 * the cards to answer "what is happening there": leads this week, campaigns
 * scheduled, findings waiting, people walking flows. Those reads reach four
 * pillars' tables (contacts in the Reach leads dataset, campaigns, the whole of
 * Sense's detectors, flow enrolments). Folding them into `GET /overview` would
 * make the Reach dashboard's one snapshot run every Sense detector on every
 * load, and tie the most-read screen to a table set its composition root argues
 * should stay small. A separate read the cards fetch on their own keeps
 * `/overview` exactly what it was.
 *
 * ## THE CARD'S `live` IS NULL WHEN THE PILLAR IS NOT AVAILABLE
 *
 * A pillar the workspace is not entitled to (or that is not built) has no
 * activity to report, and a "0 flows live" under a locked card would read as a
 * measurement of something the workspace cannot do. So `live` exists only when
 * `pillar.state === 'available'` — a refinement, not a convention.
 *
 * ## ZERO AND "NOT MEASURED" ARE DIFFERENT HERE TOO
 *
 * Reach's `leadsThisWeek` is null when the workspace has no ad account
 * connected AND has never received a lead: there is nothing that could have
 * produced one, and the card says "connect your ad account" rather than "0
 * leads". Once either is true, 0 is a measured zero. Every other figure is a
 * count of rows Wend owns outright, where 0 is always a fact.
 *
 * Counts are people, campaigns or flows — never provider rows.
 */
import { z } from 'zod';

import { isoDateTimeSchema } from '../common.js';
import { overviewPillarSchema } from './overview.js';

/** The cards' "this week": a rolling seven days, ending when the read ran. */
export const PILLAR_STATUS_WEEK_DAYS = 7;

const countSchema = z.number().int().min(0);

export const REACH_AD_ACCOUNT_STATE_VALUES = ['connected', 'not_connected', 'failed'] as const;
export const reachAdAccountStateSchema = z.enum(REACH_AD_ACCOUNT_STATE_VALUES);
export type ReachAdAccountState = z.infer<typeof reachAdAccountStateSchema>;

export const pillarLiveSchema = z.discriminatedUnion('feature', [
  z
    .object({
      feature: z.literal('reach'),
      adAccount: reachAdAccountStateSchema,
      /** People who arrived as leads in the last seven days. Null: see the header. */
      leadsThisWeek: countSchema.nullable(),
      /** Every lead the workspace holds. Null exactly when `leadsThisWeek` is. */
      leadsTotal: countSchema.nullable(),
      headline: z.string().min(1),
    })
    .strict(),
  z
    .object({
      feature: z.literal('connect'),
      /** Connect campaigns waiting for their time — never a flow's step campaign. */
      scheduled: countSchema,
      /** Connect campaigns that had a message accepted in the last seven days. */
      sentThisWeek: countSchema,
      drafts: countSchema,
      headline: z.string().min(1),
    })
    .strict(),
  z
    .object({
      feature: z.literal('sense'),
      /** Findings on Today now — ranked and not-ranked-yet, quiet ones excluded. */
      findings: countSchema,
      /** Of those, the ones that propose a flow. */
      flowProposals: countSchema,
      headline: z.string().min(1),
    })
    .strict(),
  z
    .object({
      feature: z.literal('flow'),
      live: countSchema,
      paused: countSchema,
      drafts: countSchema,
      /** Distinct people with an active or waiting enrolment in any flow. */
      peopleInFlows: countSchema,
      /** Enrolments that ended converted in the last seven days. */
      convertedThisWeek: countSchema,
      headline: z.string().min(1),
    })
    .strict(),
]);
export type PillarLive = z.infer<typeof pillarLiveSchema>;

export const pillarCardSchema = z
  .object({
    pillar: overviewPillarSchema,
    live: pillarLiveSchema.nullable(),
  })
  .strict()
  .refine((card) => (card.live === null) === (card.pillar.state !== 'available'), {
    message: 'A pillar card carries live figures exactly when the pillar is available.',
  })
  .refine((card) => card.live === null || card.live.feature === card.pillar.feature, {
    message: 'A pillar card’s live figures belong to its own pillar.',
  });
export type PillarCard = z.infer<typeof pillarCardSchema>;

export const pillarStatusResponseSchema = z
  .object({
    /** Always four, in `PILLAR_ORDER`. */
    cards: z.array(pillarCardSchema).length(4),
    weekDays: z.literal(PILLAR_STATUS_WEEK_DAYS),
    generatedAt: isoDateTimeSchema,
  })
  .strict();
export type PillarStatusResponse = z.infer<typeof pillarStatusResponseSchema>;
